/**
 * Admin service — manages user CRUD operations.
 *
 * Set FEATURES.USE_ADMIN_MOCKS = false when the backend endpoints are live.
 * Mock data covers all role/status combinations so every UI path is testable.
 */

import { apiClient } from '@/lib/apiClient';
import { FEATURES } from '@/config/appConfig';
import type {
  AdminUser,
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRequest,
  UserRole,
  UserStatus,
} from '../types/admin.types';

const USE_ADMIN_MOCKS = FEATURES.USE_ADMIN_MOCKS;

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

let mockUsers: AdminUser[] = [
  {
    user_id: 1,
    email: 'h.kotthoff@phytobiotics.com.br',
    name: 'Herbert Kotthoff',
    country_id: 2,
    country: { country_id: 2, name: 'Brazil', is_latam: true },
    status: 'active',
    created_at: '2024-01-15T10:00:00',
    user_meta: { rol: 'user', division: ['Poultry', 'Swine'], function: null, scope: null, seniority: null, job_title: 'Technical Sales Manager', language: 'Portuguese' },
    last_connected: '2026-04-29T14:23:00',
    total_interactions: 89,
  },
  {
    user_id: 2,
    email: 'drphyto@phytobiotics.com.br',
    name: 'Admin User',
    country_id: 2,
    country: { country_id: 2, name: 'Brazil', is_latam: true },
    status: 'active',
    created_at: '2024-01-10T09:00:00',
    user_meta: { rol: 'admin', division: ['Swine'], function: null, scope: null, seniority: null, job_title: 'Regional Manager', language: 'Spanish' },
    last_connected: '2026-04-30T09:11:00',
    total_interactions: 34,
  },
  {
    user_id: 3,
    email: 'b.dreves@phytobiotics.com.br',
    name: 'Beatriz Dreves',
    country_id: 2,
    country: { country_id: 2, name: 'Brazil', is_latam: true },
    status: 'active',
    created_at: '2024-02-01T08:00:00',
    user_meta: { rol: 'user', division: ['Poultry'], function: null, scope: null, seniority: null, job_title: 'Veterinary Advisor', language: 'Portuguese' },
    last_connected: '2026-04-27T16:45:00',
    total_interactions: 156,
  },
  {
    user_id: 4,
    email: 'd.otto@phytobiotics.com.br',
    name: 'Danilo Otto',
    country_id: 2,
    country: { country_id: 2, name: 'Brazil', is_latam: true },
    status: 'inactive',
    created_at: '2024-03-01T11:00:00',
    user_meta: { rol: 'user', division: ['General'], function: null, scope: null, seniority: null, job_title: '', language: '' },
    last_connected: '2026-03-16T08:02:00',
    total_interactions: 12,
  },
  {
    user_id: 5,
    email: 'conrado.torino@vaovaodata.com',
    name: 'Conrado Torino',
    country_id: 1,
    country: { country_id: 1, name: 'Argentina', is_latam: true },
    status: 'active',
    created_at: '2023-12-01T07:00:00',
    user_meta: { rol: 'super_admin', division: ['Poultry'], function: null, scope: null, seniority: null, job_title: 'CTO', language: 'English' },
    last_connected: '2026-04-30T11:58:00',
    total_interactions: 203,
  },
];

let mockNextId = 6;

// ---------------------------------------------------------------------------
// Service methods
// ---------------------------------------------------------------------------

export const adminService = {
  /**
   * List all users. Uses skipAuthErrorHandling so a 403 (non-admin) is
   * catchable without clearing the session.
   */
  getUsers: async (accessToken: string): Promise<AdminUser[]> => {
    if (USE_ADMIN_MOCKS) {
      return Promise.resolve([...mockUsers]);
    }
    return apiClient<AdminUser[]>('/admin/users', {
      headers: { Authorization: `Bearer ${accessToken}` },
      skipAuthErrorHandling: true,
    });
  },

  /**
   * Create a user in Auth0 and the database.
   * Returns minimal shape (user_id, email, name) — not the full AdminUser.
   * Backend returns 409 if the email already exists, 410 if soft-deleted.
   */
  createUser: async (accessToken: string, data: CreateUserRequest): Promise<CreateUserResponse> => {
    if (USE_ADMIN_MOCKS) {
      const existing = mockUsers.find((u) => u.email === data.email);
      if (existing) {
        const err = new Error('Email already exists');
        (err as Error & { status?: number }).status = 409;
        return Promise.reject(err);
      }
      const id = mockNextId++;
      const response: CreateUserResponse = {
        user_id: id,
        email: data.email,
        name: data.name ?? '',
      };
      return Promise.resolve(response);
    }
    return apiClient<CreateUserResponse>('/admin/users', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: data,
    });
  },

  /**
   * Activate or suspend a user.
   */
  toggleAuthorization: async (
    accessToken: string,
    userId: number,
    status: UserStatus,
  ): Promise<void> => {
    if (USE_ADMIN_MOCKS) {
      mockUsers = mockUsers.map((u) =>
        u.user_id === userId ? { ...u, status } : u,
      );
      return Promise.resolve();
    }
    await apiClient<{ message: string }>(`/admin/users/${userId}/authorization`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: { status },
    });
  },

  /**
   * Change user role. Requires super_admin token.
   * Uses PATCH /admin/users/{id} with { rol }.
   */
  updateRole: async (
    accessToken: string,
    userId: number,
    rol: UserRole,
  ): Promise<void> => {
    if (USE_ADMIN_MOCKS) {
      mockUsers = mockUsers.map((u) =>
        u.user_id === userId ? { ...u, user_meta: { ...u.user_meta, rol } } : u,
      );
      return Promise.resolve();
    }
    await apiClient<{ message: string }>(`/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: { rol },
      skipAuthErrorHandling: true,
    });
  },

  /**
   * Update user profile fields.
   * Uses PATCH /admin/users/{id}.
   */
  updateUser: async (
    accessToken: string,
    userId: number,
    data: UpdateUserRequest,
  ): Promise<void> => {
    if (USE_ADMIN_MOCKS) {
      mockUsers = mockUsers.map((u) =>
        u.user_id === userId
          ? {
              ...u,
              ...(data.name !== undefined && { name: data.name }),
              ...(data.country_id !== undefined && {
                country_id: data.country_id,
                country: { ...u.country, country_id: data.country_id },
              }),
              ...((data.division !== undefined || data.job_title !== undefined || data.language !== undefined || data.function !== undefined || data.scope !== undefined || data.seniority !== undefined) && {
                user_meta: {
                  ...u.user_meta,
                  ...(data.division !== undefined && { division: data.division }),
                  ...(data.job_title !== undefined && { job_title: data.job_title }),
                  ...(data.language !== undefined && { language: data.language }),
                  ...(data.function !== undefined && { function: data.function }),
                  ...(data.scope !== undefined && { scope: data.scope }),
                  ...(data.seniority !== undefined && { seniority: data.seniority }),
                },
              }),
            }
          : u,
      );
      return Promise.resolve();
    }
    await apiClient<{ message: string }>(`/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: data,
      skipAuthErrorHandling: true,
    });
  },

  /**
   * Soft-delete a user (sets inactive in the backend).
   * Returns 403 if the user is an admin — remove admin role first.
   */
  deleteUser: async (accessToken: string, userId: number): Promise<void> => {
    if (USE_ADMIN_MOCKS) {
      mockUsers = mockUsers.filter((u) => u.user_id !== userId);
      return Promise.resolve();
    }
    await apiClient<{ message: string }>(`/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
      skipAuthErrorHandling: true,
    });
  },
};
